import { createSelector } from 'reselect'
import differenceInMonths from 'date-fns/differenceInMonths'
import format from 'date-fns/format'
import isAfter from 'date-fns/isAfter'
import isBefore from 'date-fns/isBefore'
import startOfDay from 'date-fns/startOfDay'
import truncate from 'lodash/truncate'
import words from 'lodash/words'

import { formatRemuneration } from '@offerzen/pinata-shame'
import { formatSmartTimestamp, formatDistanceInWords } from '@offerzen/utils'

import { getCandidateAvailabilityMap } from '@/core/reducks/candidateAvailabilities'
import { parseDate } from '@/core/helpers/dateTimeHelper'
import { getCandidateNoteMap } from '@/core/reducks/candidateNotes'
import { getGreenhouseProfileMap } from '@/core/reducks/greenhouseProfiles'
import { getInterviewRequestItem } from '@/core/reducks/interviewRequests'
import { getInterviewRequestEventFeedbackMap } from '@/core/reducks/interviewRequestEventFeedbacks'
import { getInterviewRequestEventMap } from '@/core/reducks/interviewRequestEvents'
import { getInterviewRequestMessageMap } from '@/core/reducks/interviewRequestMessages'
import { getMemberProfileMap } from '@/core/reducks/memberProfiles'
import { getAccountManagerMap } from '@/core/reducks/accountManagers'

import { memberProfileId, userRole } from '@/core/selectors/currentUser'
import { urlFilters } from '@/core/selectors/urlFilters'

import { interviewEventCategoryMap, interviewEventStatusMap, InterviewEventStatus } from '../helpers/interviewEventHelpers'
import { interviewRequestStatusMap } from '../helpers/interviewRequestHelpers'
import { getInterviewRequestDetailsView } from '../reducks/interviewRequestDetails'
import { getInterviewRequestOfferMap } from '@/core/reducks/interviewRequestOffers'
import { getUnavailableCandidateNotificationStatus as getNotificationMessage } from '../helpers/unavailableCandidateNotificationsHelpers'

export const loadingInterviewRequests = () =>
  createSelector([getInterviewRequestDetailsView], view => view.fetchingInterviewRequest || !view.lastUpdatedInterviewRequest)

export const interviewRequestNotFound = () => createSelector([getInterviewRequestDetailsView], view => !!view.fetchInterviewRequestError)

export const isInterviewRequestStale = () =>
  createSelector([getInterviewRequestItem], ({ created_at }) => differenceInMonths(Date.now(), parseDate(created_at)) >= 3)

export const interviewRequestDetails = () =>
  createSelector([getInterviewRequestItem], ({ status, equity, salary, member_profile, position, application, ...interviewRequest }) => {
    return {
      id: interviewRequest.id,
      status,
      statusLabel: interviewRequestStatusMap[status] ? interviewRequestStatusMap[status].label : '-',
      application,
      atsStatus: interviewRequest.ats_status,
      archived: interviewRequest.archived,
      createdAtTimestamp: interviewRequest?.created_at && format(parseDate(interviewRequest.created_at), 'd MMMM yyyy'),
      equity: equity ? equity.toLowerCase() : null,
      salary: salary
        ? formatRemuneration({
            currencySymbol: interviewRequest.currency_symbol,
            amount: salary,
            remunerationPeriod: interviewRequest.remuneration_period,
          })
        : null,
      memberProfileName: member_profile ? member_profile.name : null,
      position,
      stale: differenceInMonths(Date.now(), parseDate(interviewRequest.created_at)) >= 3,
      unavailableReason: interviewRequest.unavailable_reason ? getNotificationMessage(interviewRequest.unavailable_reason) : null,
      markedUnavailableAt: interviewRequest.marked_unavailable_at ? formatDistanceInWords(interviewRequest.marked_unavailable_at) : null,
      workplacePolicy: interviewRequest.workplace_policy,
      application,
    }
  })

export const interviewRequestCandidate = () =>
  createSelector([getInterviewRequestItem], ({ candidate_profile, is_candidate_visible_to_company }) => {
    if (!candidate_profile) return null
    const { id, slug, name, profile_image_url, log_line, phone, email } = candidate_profile
    return {
      id,
      slug,
      name,
      firstName: words(name)[0],
      avatar: profile_image_url,
      logLine: log_line,
      isCandidateVisibleToCompany: is_candidate_visible_to_company,
      phoneNumber: phone,
      email,
    }
  })

export const greenhouseProfileDetails = () =>
  createSelector([getInterviewRequestDetailsView, getGreenhouseProfileMap], ({ greenhouseProfile }, greenhouseMap) => {
    if (!greenhouseProfile) return null

    const profile = greenhouseMap[greenhouseProfile]
    return {
      href: profile.url,
      status: profile.status || 'Assign to Job in Greenhouse',
    }
  })

export const archivingInterviewRequest = () => createSelector([getInterviewRequestDetailsView], view => view.archivingInterviewRequest)

export const candidateAvailabilities = () =>
  createSelector([getInterviewRequestDetailsView, getCandidateAvailabilityMap], ({ candidateAvailabilities }, availabilityMap) =>
    candidateAvailabilities.reduce((map, availabilityId) => {
      const { start_at, ...availability } = availabilityMap[availabilityId]
      const date = startOfDay(parseDate(start_at))

      if (!map[date]) {
        map[date] = []
      }

      map[date] = [
        ...map[date],
        {
          id: availabilityId,
          startDate: start_at,
          endDate: availability.end_at,
        },
      ]
      return map
    }, {}),
  )

export const hasAvailabilities = () =>
  createSelector([getInterviewRequestDetailsView], ({ candidateAvailabilities }) => candidateAvailabilities.length > 0)

export const initialMessage = () =>
  createSelector([getInterviewRequestItem], ({ member_profile, message, ...interviewRequest }) => ({
    creatorAvatar: member_profile.profile_image_url,
    creatorName: member_profile.name,
    createdTimestamp: formatSmartTimestamp(interviewRequest.created_at),
    initialMessage: message,
    initialMessageExcerpt: truncate(message, { length: 1500 }),
    initialMessageReadMoreEnabled: message && message.length > 1500,
  }))

export const loadingMessages = () =>
  createSelector(
    [getInterviewRequestDetailsView],
    view => !view.lastUpdatedMessages || !view.lastUpdatedInterviewEvents || !view.lastUpdatedOffers,
  )

export const messagesList = () =>
  createSelector(
    [
      getInterviewRequestItem,
      getInterviewRequestDetailsView,
      getInterviewRequestMessageMap,
      getInterviewRequestOfferMap,
      getInterviewRequestEventMap,
      getMemberProfileMap,
    ],
    ({ candidate_profile, ...interviewRequest }, { messages, offers, interviewEvents }, messageMap, offerMap, eventMap) => {
      const candidateName = candidate_profile ? words(candidate_profile.name)[0] : ''

      const firstResponse =
        messageMap &&
        Object.values(messageMap)
          .reverse()
          .find(message => message.created_by.profile_type === 'candidate')

      const mappedMessages = messages.map(messageId => {
        const { created_by, attachments, ...message } = messageMap[messageId]

        return {
          id: messageId,
          messageType: 'message',

          candidateName,
          avatar: interviewRequest.is_candidate_visible_to_company ? created_by.profile_image_url : null,
          name: created_by.name,

          date: message.created_at,
          timestamp: formatSmartTimestamp(message.created_at),

          readMoreEnabled: message.text && message.text.length > 1500,
          text: message.text,
          textExcerpt: truncate(message.text, { length: 1500 }),
          isFirstResponse: firstResponse && firstResponse.id === messageId,
          attachments:
            attachments.length > 0
              ? attachments.map(attachment => ({
                  id: attachment.id,
                  name: attachment.file.name,
                  url: `/company/interview_requests/${interviewRequest.id}/message_attachments/${attachment.id}`,
                }))
              : null,
        }
      })

      const mappedOffers = offers.map(offerId => {
        const { created_by, attachments, ...offer } = offerMap[offerId]
        return {
          id: offerId,
          messageType: 'offer',

          candidateName,
          avatar: created_by.profile_image_url,
          name: words(created_by.name)[0],

          date: offer.created_at,
          timestamp: formatSmartTimestamp(offer.created_at),

          readMoreEnabled: offer.text && offer.text.length > 1500,
          text: offer.text,
          textExcerpt: truncate(offer.text, { length: 1500 }),
          attachments:
            attachments.length > 0
              ? attachments.map(attachment => ({
                  id: attachment.id,
                  name: attachment.file.name,
                  url: `/company/interview_requests/${interviewRequest.id}/message_attachments/${attachment.id}`,
                }))
              : null,
        }
      })

      const mappedEvents = interviewEvents.map(eventId => {
        const { status, memberProfile, startAt, id, createdAt, candidateProfile, endAt, category, ...event } = eventMap[eventId]

        const { details, label } = interviewEventCategoryMap[category] || {}
        const eventStatus = interviewEventStatusMap[status]

        const memberName = memberProfile ? words(memberProfile.name)[0] : 'Member'

        const extraDetails = details
          ? details.reduce((map, item) => {
              map[item] = event[item]
              return map
            }, {})
          : {}

        return {
          id: eventId,
          messageType: 'event',
          candidateName: candidateProfile ? words(candidateProfile.name)[0] : '',
          name: memberName,
          date: createdAt,
          timestamp: formatSmartTimestamp(createdAt),
          status,
          eventStatus: status,
          ...(eventStatus && {
            eventStatusLabel: eventStatus.label,
            eventStatusIcon: eventStatus.icon,
            eventStatusTheme: eventStatus.theme,
            eventStatusColour: eventStatus.colour,
          }),
          eventType: label.toLowerCase() || '-',
          eventDate: startAt && format(parseDate(startAt), 'EEEE, d MMMM yyyy'),
          eventStartTime: startAt,
          eventEndTime: endAt,
          eventMemberName: memberName,
          eventExtraDetails: extraDetails,
        }
      })

      return [...mappedMessages, ...mappedOffers, ...mappedEvents].sort((messageA, messageB) => {
        if (isAfter(parseDate(messageA.date), parseDate(messageB.date))) return -1
        if (isAfter(parseDate(messageB.date), parseDate(messageA.date))) return 1
        return 0
      })
    },
  )

export const creatingMessage = () => createSelector([getInterviewRequestDetailsView], view => view.creatingMessage)

export const competingInterviewRequestsCount = () =>
  createSelector([getInterviewRequestDetailsView], ({ competingInterviewRequests }) => {
    return competingInterviewRequests ? competingInterviewRequests.length : 0
  })

export const interviewRequestToWithdraw = () =>
  createSelector([getInterviewRequestItem, getInterviewRequestDetailsView], (interviewRequest, { withdrawingInterviewRequest }) => {
    const { candidate_profile } = interviewRequest

    return {
      id: interviewRequest.id,
      withdrawingInterviewRequest,
      ...(candidate_profile && {
        avatar: candidate_profile.profile_image_url,
        name: candidate_profile.name,
      }),
    }
  })

export const loadingOffers = () => createSelector([getInterviewRequestDetailsView], view => !view.lastUpdatedOffers)

export const offersList = () =>
  createSelector(
    [getInterviewRequestItem, getInterviewRequestDetailsView, getInterviewRequestOfferMap],
    (interviewRequest, { offers }, offerMap) =>
      offers.map(offerId => {
        const { attachments, ...offer } = offerMap[offerId]
        return {
          id: offerId,

          timestamp: format(parseDate(offer.created_at), "d MMMM yyyy 'at' h:mma"),

          readMoreEnabled: offer.text && offer.text.length > 1500,
          text: offer.text,
          textExcerpt: truncate(offer.text, { length: 1500 }),

          attachments:
            attachments.length > 0
              ? attachments.map(attachment => ({
                  id: attachment.id,
                  name: attachment.file.name,
                  url: `/company/interview_requests/${interviewRequest.id}/message_attachments/${attachment.id}`,
                }))
              : null,
        }
      }),
  )

export const extendingOffer = () => createSelector([getInterviewRequestDetailsView], view => !!view.creatingOffer)

export const schedulingInterviewEvent = () => createSelector([getInterviewRequestDetailsView], view => !!view.creatingInterviewEvent)

export const fetchingCandidateAvailabilities = () =>
  createSelector([getInterviewRequestDetailsView], view => view.fetchingCandidateAvailabilities)

export const fetchingInterviewEvents = () => createSelector([getInterviewRequestDetailsView], view => view.fetchingInterviewEvents)

export const loadingInterviewEvents = () => createSelector([getInterviewRequestDetailsView], view => !view.lastUpdatedInterviewEvents)

export const hasInterviewEvents = () =>
  createSelector(
    [getInterviewRequestDetailsView],
    view => (view.lastUpdatedInterviewEvents && view.interviewEvents.length > 0) || !view.lastUpdatedInterviewEvents,
  )

const filterUpcomingInterviewEvents = interviewEvent => {
  return (
    isAfter(parseDate(interviewEvent.endAt), new Date()) &&
    ![InterviewEventStatus.Cancelled, InterviewEventStatus.Declined].includes(interviewEvent.status)
  )
}

const sortInterviewEvents = (eventA, eventB) => {
  if (!eventA.startAt && !eventB.startAt) {
    // Both events have no startAt date
    // Sort by createdAt ascending
    if (isAfter(parseDate(eventA.createdAt), parseDate(eventB.createdAt))) return 1
    if (isAfter(parseDate(eventB.createdAt), parseDate(eventA.createdAt))) return -1
  }

  if (!eventA.startAt) {
    // Event A has no startAt date but event B does
    // Place event A before event B
    return -1
  }

  if (!eventB.startAt) {
    // Event B has no startAt date but event A does
    // Place event B before event A
    return 1
  }

  // Both events have startAt dates
  // Sort by startAt ascending
  if (isAfter(parseDate(eventA.startAt), parseDate(eventB.startAt))) return 1
  if (isAfter(parseDate(eventB.startAt), parseDate(eventA.startAt))) return -1

  return 0
}

export const upcomingInterviewEvents = () =>
  createSelector([getInterviewRequestDetailsView, getInterviewRequestEventMap], ({ interviewEvents, interviewEventStats }, eventMap) => {
    return interviewEvents
      .map(eventId => eventMap[eventId])
      .filter(event => filterUpcomingInterviewEvents(event))
      .sort((eventA, eventB) => sortInterviewEvents(eventA, eventB))
      .map(({ status, startAt, memberProfile, candidateProfile, category, id, endAt, googleCalendarLink, title, poweredBy, ...event }) => {
        const eventStatus = interviewEventStatusMap[status]
        const { details, label } = interviewEventCategoryMap[category] || {}
        const extraDetails = details
          ? details.reduce((map, item) => {
              map[item] = event[item]
              return map
            }, {})
          : {}

        const candidateName = candidateProfile ? words(candidateProfile.name)[0] : ''
        const memberName = memberProfile ? words(memberProfile.name)[0] : 'Member'

        return {
          id,
          status,
          ...(eventStatus && {
            statusLabel: eventStatus.label,
            statusIcon: eventStatus.icon,
            statusTheme: eventStatus.theme,
            statusColour: eventStatus.colour,
          }),

          candidateName,
          memberName,
          date: startAt && format(parseDate(startAt), 'EEEE, d MMMM yyyy'),
          startTime: startAt,
          endTime: endAt,
          googleCalendarLink,
          cancelling: interviewEventStats[id].updating,
          type: label || '-',
          category,
          poweredBy,
          title,
          ...extraDetails,
        }
      })
  })

export const pastInterviewEvents = () =>
  createSelector(
    [getInterviewRequestDetailsView, getInterviewRequestEventMap, getInterviewRequestEventFeedbackMap, memberProfileId],
    ({ interviewEvents, interviewEventStats, lastUpdatedEventFeedbacks }, eventMap, eventFeedbackMap, currentMember) => {
      return interviewEvents
        .map(eventId => eventMap[eventId])
        .filter(
          event =>
            (event.endAt && isBefore(parseDate(event.endAt), new Date())) ||
            [InterviewEventStatus.Cancelled, InterviewEventStatus.Declined].includes(event.status),
        )
        .sort((eventA, eventB) => sortInterviewEvents(eventA, eventB))
        .map(({ status, memberProfile, id, category, candidateProfile, startAt, endAt, title }) => {
          const interviewType = interviewEventCategoryMap[category]
          const eventStatus = interviewEventStatusMap[status]

          const { eventFeedbackId } = interviewEventStats[id]
          const eventFeedback = eventFeedbackId ? eventFeedbackMap[eventFeedbackId] : null

          const candidateName = candidateProfile ? words(candidateProfile.name)[0] : ''

          return {
            id,
            status,
            ...(eventStatus && {
              statusLabel: eventStatus.label,
              statusIcon: eventStatus.icon,
              statusTheme: eventStatus.theme,
              statusColour: eventStatus.colour,
            }),
            eventFeedbackId,
            ...(eventFeedback && {
              rating: new Array(eventFeedback.rating).fill(0),
              comment: eventFeedback.comment,
              commentExcerpt: truncate(eventFeedback.comment, { length: 150 }),
              readMoreEnabled: eventFeedback.comment && eventFeedback.comment.length > 150,
              tookPlace: !!eventFeedback.took_place,
            }),
            waitingForFeedback:
              !eventFeedbackId && (!memberProfile || memberProfile?.id !== currentMember) && status === InterviewEventStatus.Confirmed,
            canGiveFeedback:
              !!lastUpdatedEventFeedbacks &&
              !eventFeedbackId &&
              memberProfile?.id === currentMember &&
              status === InterviewEventStatus.Confirmed,
            candidateName,
            type: interviewType ? interviewType.label : '-',
            date: startAt && format(parseDate(startAt), 'd MMMM yyyy'),
            startTime: startAt,
            endTime: endAt,
            title,
          }
        })
    },
  )

export const pastEventToGiveFeedback = () =>
  createSelector([getInterviewRequestDetailsView, getInterviewRequestEventMap], ({ eventToGiveFeedback }, eventMap) => {
    const event = eventMap[eventToGiveFeedback]
    if (!event) return null

    const interviewType = interviewEventCategoryMap[event.category] || {}
    return {
      id: eventToGiveFeedback,
      date: format(parseDate(event.startAt), 'EEEE, d MMMM yyyy'),
      startTime: event.startAt,
      endTime: event.endAt,
      interviewType: interviewType.label || '-',
    }
  })

export const creatingEventFeedback = () =>
  createSelector([getInterviewRequestDetailsView], ({ eventToGiveFeedback, interviewEventStats }) =>
    eventToGiveFeedback ? interviewEventStats[eventToGiveFeedback].creatingEventFeedback : false,
  )

const accountManagerMentionsList = createSelector(
  [getInterviewRequestDetailsView, getAccountManagerMap],
  ({ accountManagers }, accountManagerMap) =>
    accountManagers.map(accountManagerId => {
      const member = accountManagerMap[accountManagerId]
      return {
        handle: member.notes_handle,
        name: member.name,
        avatar: member.profile_image_url,
        accountManager: true,
      }
    }),
)

const membersMentionList = createSelector([getInterviewRequestDetailsView, getMemberProfileMap], ({ members }, memberMap) =>
  members.map(memberId => {
    const member = memberMap[memberId]
    return {
      handle: member.slug,
      name: member.name,
      avatar: member.profile_image_url,
      accountManager: false,
    }
  }),
)

export const fullMentionsList = () =>
  createSelector([accountManagerMentionsList, membersMentionList], (accountManagers, members) =>
    accountManagers.concat(members).filter(o => o.handle),
  )

export const loadingTeamNotes = () => createSelector([getInterviewRequestDetailsView], view => !view.lastUpdatedTeamNotes)

export const deletingTeamNote = () => createSelector([getInterviewRequestDetailsView], view => view.deletingTeamNote)

export const lastUpdatedTeamNotes = () => createSelector([getInterviewRequestDetailsView], view => view.lastUpdatedTeamNotes)

export const teamNotesList = () =>
  createSelector(
    [getInterviewRequestDetailsView, getCandidateNoteMap, memberProfileId, userRole],
    ({ teamNotes }, noteMap, memberProfileId, userRole) =>
      teamNotes.map(noteId => {
        const noteEntity = noteMap[noteId]
        if (!noteEntity) return { id: noteId, deleted: true }

        const { created_by, ...note } = noteEntity
        // deal with deleted users
        const createdBy = created_by || { profile_image: null, name: 'OfferZen User', id: null }

        return {
          id: noteId,
          avatar: createdBy.profile_image || null,
          name: createdBy.name,
          edited: note.edited,
          showActions: createdBy.id === memberProfileId || userRole === 'offerzen_user',
          timestamp: formatSmartTimestamp(note.created_at),
          readMoreEnabled: note.text && note.text.length > 150,
          textExcerpt: truncate(note.text, { length: 150 }),
          text: note.text,
        }
      }),
  )

export const loadingEventsAndFeedbacks = () =>
  createSelector([getInterviewRequestDetailsView], view => !view.lastUpdatedInterviewEvents || !view.lastUpdatedEventFeedbacks)

export const validEventIdForFeedbackForm = () =>
  createSelector(
    [getInterviewRequestDetailsView, urlFilters, getInterviewRequestEventMap, memberProfileId],
    ({ lastUpdatedInterviewEvents, lastUpdatedEventFeedbacks, interviewEventStats }, filters, eventMap, currentUserId) => {
      const eventIdFromUrl = filters.feedback_for

      if (!eventIdFromUrl) return null

      if (!lastUpdatedInterviewEvents || !lastUpdatedEventFeedbacks) return null

      const event = eventMap[eventIdFromUrl]

      if (!event) return null
      if (event.status !== InterviewEventStatus.Confirmed) return null

      if (event.memberProfile.id !== currentUserId) return null

      const { eventFeedbackId } = interviewEventStats[eventIdFromUrl]
      if (eventFeedbackId) return null

      return eventIdFromUrl
    },
  )
